import React, {createContext, useContext, useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {format, parseISO} from 'date-fns';
import {
  createColumnHelper,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  Table,
  useReactTable
} from '@tanstack/react-table';
import classNames from 'classnames';
import ButtonDetails from '../ButtonDetails';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import Spinner from '../../../../common/components/ui/Spinner';
import {TransferItem} from '../../types';
import {ExcelExportService} from '../../../../services';
import {SystemSocialParams} from '../../../../common/types';
import {Format} from '../../../../common/utils';
import styles from '../../../../common/styles/styles.module.scss';

export type TransferRowExtended = TransferItem & {actions: unknown};

export type TransfersContext = {
  tableUnpaidReceipts: Table<TransferRowExtended>
  totalCountUnpaidReceipts: number
  tableTransfers: Table<TransferRowExtended>
  totalCountTransfers: number
  saveListToExcelFile: () => void
  fileExists: boolean
  year: string,
  systemSocialParams: SystemSocialParams,
}

export type TransfersContextProviderProps = {
  children: React.ReactNode,
}

export type LinesItemType = {
  [key: string]: unknown | any
}

export const defaultContext: TransfersContext = {
  tableUnpaidReceipts: {} as Table<TransferRowExtended>,
  totalCountUnpaidReceipts: 0,
  tableTransfers: {} as Table<TransferRowExtended>,
  totalCountTransfers: 0,
  saveListToExcelFile: () => {},
  fileExists: false,
  year: '',
  systemSocialParams: {} as SystemSocialParams
};

export const TransfersContext = createContext<TransfersContext>(defaultContext);
TransfersContext.displayName = 'TransfersContext';

export const useTransfersContext = () => useContext(TransfersContext);

export const TransfersContextProvider = ({children}: TransfersContextProviderProps) => {
  const intl = useIntl();
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();

  const [unpaidReceiptsList, setUnpaidReceiptsList] = useState<TransferRowExtended[]>([]);
  const [transfersList, setTransfersList] = useState<TransferRowExtended[]>([]);
  const [metaData, setMetaData] = useState({exists: false, year: ''});
  const [systemSocialParams, setSystemSocialParams] = useState<SystemSocialParams>({} as SystemSocialParams);
  const [loading, setLoading] = useState(true);

  const updateLists = async () => {
    try {
      const result = await apiService.getTransfersList();

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        setUnpaidReceiptsList([...result.body.unpaidRealReceiptLines, ...result.body.unpaidVirtualReceiptLines] as TransferRowExtended[]);
        setTransfersList([...result.body.transfers.list] as TransferRowExtended[]);
        setMetaData(result.body.meta.form101FileStatus);
        setSystemSocialParams(result.body.meta.systemSocialParams);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };

  useEffect(() => {
    setLoading(true);
    updateLists().finally(() => setLoading(false));
  }, []);

  const columnHelper = createColumnHelper<TransferRowExtended>();
  const columnsReceipts = [
    columnHelper.accessor('_id', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="transfers-col-receiptID" defaultMessage="Receipt ID"/>,
    }),
    columnHelper.accessor('customerName', {
      cell: info => {
        const customer = info.cell.row.original.receipt.customer;
        return [
          customer.name.first,
          customer.name.last
        ].filter(Boolean).join(' ');
      },
      header: () => <FormattedMessage id="transfers-col-customerName" defaultMessage="Customer name"/>,
    }),
    columnHelper.accessor('date', {
      cell: info => {
        return Boolean(info.cell.row.original.dueDate)
          ? format(parseISO(info.cell.row.original.dueDate), 'dd/MM/yyyy')
          : '';
      },
      header: () => <FormattedMessage id="transfers-col-date" defaultMessage="Created At"/>,
    }),
    columnHelper.accessor('amount', {
      cell: info => {
        const value = (typeof info.cell.row.original.amount === 'number')
          ? info.cell.row.original.amount
          : parseFloat(info.cell.row.original.amount);
        return Format.displayFloatThousandsWithCommas(value) || '';
      },
      header: () => <FormattedMessage id="transfers-col-amount" defaultMessage="Amount"/>,
    }),
    columnHelper.accessor('paymentTypeName', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="transfers-col-paymentType" defaultMessage="Payment type"/>,
    })
  ];
  const columnsTransfers = [
    columnHelper.accessor('paycheck', {
      cell: info => {
        const datetime = info.cell.row.original.datetime;
        const paycheck = [
          intl.formatMessage({
            id: 'transfers-col-paycheck',
            defaultMessage: 'Paycheck '
          }),
          info.getValue().date?.month,
          '.',
          info.getValue().date?.year
        ].filter(Boolean);
        const date = Boolean(datetime)
          ? format(parseISO(datetime), 'dd/MM/yyyy')
          : '';

        return (
          <>
            <div>{date}</div>
            <div>{paycheck}</div>
          </>
        );
      },
      header: () => ''
    }),
    columnHelper.accessor('receiptID', {
      cell: info => {
        const mapReceiptIDs = (row: TransferRowExtended) => row.lines
          .map((item, index) => <div key={index}>{item._id}</div>);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={styles.pointer}>
              {info.row.getIsExpanded() ? mapReceiptIDs(info.cell.row.original) : '...'}
            </div>
          )
          : mapReceiptIDs(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-receiptID" defaultMessage="Receipt ID"/>,
    }),
    columnHelper.accessor('customerName', {
      cell: info => {
        const mapCustomerNames = (row: TransferRowExtended) => ([
          ...row.lines.map((item, index) => {
            const customerName = [
              item.receipt.customer._id,
              '-',
              item.receipt.customer.name.first,
              item.receipt.customer.name.last,
            ].filter(Boolean).join(' ');

            return <div key={index}>{customerName}</div>;
          }),
          ...row.customLines.map((item, index) => {
            return <div key={index + info.cell.row.original.lines.length}>{item.customer}</div>;
          })
        ]);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={classNames(styles.pointer, {[styles.italic]: !info.row.getIsExpanded()})}>
              {info.row.getIsExpanded()
                ? mapCustomerNames(info.cell.row.original)
                : <FormattedMessage id="transfers-col-expand" defaultMessage="expand to view all lines"/>}
            </div>
          )
          : mapCustomerNames(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-customerName" defaultMessage="Customer name"/>,
    }),
    columnHelper.accessor('receiptIDCustomerName', {
      cell: info => {
        const mapReceiptIDs = (row: TransferRowExtended) => ([
          ...row.lines.map((item, index) => {
            const customerName = [
              item.receipt.customer._id,
              '-',
              item.receipt.customer.name.first,
              item.receipt.customer.name.last,
            ].filter(Boolean).join(' ');
            return (
              <div key={index}>
                <div>{item._id}</div>
                <div>{customerName}</div>
              </div>
            );
          }),
          ...row.customLines.map((item, index) => {
            return (
              <div key={index}>
                <div>{item.customer}</div>
              </div>
            );
          }),
        ]);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={styles.pointer}>
              {info.row.getIsExpanded()
                ? mapReceiptIDs(info.cell.row.original)
                : <FormattedMessage id="transfers-col-expand" defaultMessage="expand to view all lines"/>}
            </div>
          )
          : mapReceiptIDs(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-receiptID" defaultMessage="Receipt ID"/>,
    }),
    columnHelper.accessor('createdAt', {
      cell: info => {
        const mapCreationDates = (row: TransferRowExtended) => ([
          ...row.lines.map((item, index) => {
            const date = Boolean(item.receipt.date)
              ? format(parseISO(item.receipt.date), 'dd/MM/yyyy')
              : '';

            return <div key={index}>{date}</div>;
          }),
          ...row.customLines.map((item, index) => {
            return <div key={index + info.cell.row.original.lines.length}>{item.creationDate}</div>;
          }),
        ]);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={styles.pointer}>
              {info.row.getIsExpanded() ? mapCreationDates(info.cell.row.original) : '...'}
            </div>
          )
          : mapCreationDates(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-createdAt" defaultMessage="Created At"/>,
    }),
    columnHelper.accessor('amount', {
      cell: info => {
        const mapAmounts = (row: TransferRowExtended) => ([
          ...row.lines.map((item, index) => {
            const amount = Format.displayFloatThousandsWithCommas(parseFloat(item.amount)) || '';

            return <div key={index}>{amount}</div>;
          }),
          ...row.customLines.map((item, index) => {
            const amount = Format.displayFloatThousandsWithCommas(item.amount) || '';

            return <div key={index + info.cell.row.original.lines.length}>{amount}</div>;
          }),
        ]);
        const total = [
          ...info.cell.row.original.lines.map(item => typeof item.amount === 'number' ? item.amount : parseFloat(item.amount)),
          ...info.cell.row.original.customLines.map(item => typeof item.amount === 'number' ? item.amount : parseFloat(item.amount)),
        ].reduce((acc, amount) => {
          return isNaN(amount) ? acc : amount + acc
        }, 0);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={styles.pointer}>
              {info.row.getIsExpanded() ? mapAmounts(info.cell.row.original) : Format.displayFloatThousandsWithCommas(total)}
            </div>
          )
          : mapAmounts(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-amount" defaultMessage="Amount"/>,
    }),
    columnHelper.accessor('paymentTypeName', {
      cell: info => {
        const mapPaymentTypes = (row: TransferRowExtended) => ([
          ...row.lines.map((item, index) => <div key={index}>{item.paymentTypeName}</div>),
          ...row.customLines.map((item, index) => <div key={index}>{item.paymentType}</div>),
        ]);

        return ([...info.cell.row.original.lines, ...info.cell.row.original.customLines].length > 1)
          ? (
            <div onClick={() => info.cell.row.toggleExpanded()} className={styles.pointer}>
              {info.row.getIsExpanded() ? mapPaymentTypes(info.cell.row.original) : '...'}
            </div>
          )
          : mapPaymentTypes(info.cell.row.original);
      },
      header: () => <FormattedMessage id="transfers-col-paymentType" defaultMessage="Payment type"/>,
    }),
    columnHelper.accessor('actions', {
      cell: info => (
        <div><ButtonDetails systemSocialParams={systemSocialParams} row={info.cell.row.original}/></div>
      ),
      header: () => ''
    }),
  ];

  const tableUnpaidReceipts = useReactTable({
    data: unpaidReceiptsList,
    columns: columnsReceipts,
    getCoreRowModel: getCoreRowModel(),
  });

  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const tableTransfers = useReactTable({
    data: transfersList,
    columns: columnsTransfers,
    state: {expanded},
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const saveListToExcelFile = async () => {
    try {
      const paycheck = intl.formatMessage({id: 'transfers-col-paycheck', defaultMessage: 'Paycheck'});
      const receiptID = intl.formatMessage({id: 'transfers-col-receiptID', defaultMessage: 'Receipt ID'});
      const customerID = intl.formatMessage({id: 'transfers-col-customerID', defaultMessage: 'Customer ID'});
      const customerName = intl.formatMessage({id: 'transfers-col-customerName', defaultMessage: 'Customer name'});
      const createdAt = intl.formatMessage({id: 'transfers-col-createdAt', defaultMessage: 'Created At'});
      const amount = intl.formatMessage({id: 'transfers-col-amount', defaultMessage: 'Amount'});
      const paymentType = intl.formatMessage({id: 'transfers-col-paymentType', defaultMessage: 'Payment type'});

      const {body: {transfers}} = await apiService.getTransfersList();
      const {list} = transfers;
      const data = [
        ...list.map(item => {
          return {
            ['']: format(parseISO(`${item.datetime}`), 'dd/MM/yyyy'),
            [paycheck]: `${item.paycheck.date.month}.${item.paycheck.date.year}`,
            [receiptID]: item.lines
              .map((receipt: LinesItemType) => receipt._id)
              .filter(Boolean).join(`\n`),
            [customerID]: item.lines
              .map((item: LinesItemType) => item.receipt.customer._id)
              .filter(Boolean).join(`\n`),
            [customerName]: [
              ...item.lines.map((item: LinesItemType) => {
                return [
                  item.receipt.customer.name.first,
                  item.receipt.customer.name.last,
                ].filter(Boolean).join(' ');
              }),
              ...item.customLines.map((item: LinesItemType) => {
                return item.customer;
              })
            ].filter(Boolean).join(`\n`),
            [createdAt]: [
              ...item.lines.map((item: LinesItemType) => {
                return Boolean(item.receipt.date)
                  ? format(parseISO(item.receipt.date), 'dd/MM/yyyy')
                  : '';
              }),
              ...item.customLines.map((item: LinesItemType) => {
                return item.creationDate;
              })
            ].filter(Boolean).join(`\n`),
            [amount]: [
              ...item.lines.map((item: LinesItemType) => {
                return item.amount || '';
              }),
              ...item.customLines.map((item: LinesItemType) => {
                return item.amount || '';
              })
            ].filter(Boolean).join(`\n`),
            [paymentType]: [
              ...item.lines.map((item: LinesItemType) => {
                return item.paymentTypeName || '';
              }),
              ...item.customLines.map((item: LinesItemType) => {
                return item.paymentType || '';
              })
            ].filter(Boolean).join(`\n`),
          };
        })
      ];
      const excelExport = new ExcelExportService();
      excelExport
        .addPage(data, '', true)
        .generateFile('העברות.xlsx');

    } catch (e) {
      globalMessage.open(M.serverError);
    }
  };

  const context: TransfersContext = {
    ...defaultContext,
    tableUnpaidReceipts,
    totalCountUnpaidReceipts: unpaidReceiptsList.length,
    tableTransfers,
    totalCountTransfers: transfersList.length,
    saveListToExcelFile,
    fileExists: metaData.exists,
    year: metaData.year,
    systemSocialParams
  };

  return loading
    ? <Spinner/>
    : (
      <TransfersContext.Provider value={context}>
        {children}
      </TransfersContext.Provider>
    );
};
